import { handleResponseOld, handleResponseWithNon200Errors, makeGet } from './helpers';
import { ApprovalStatus } from '@/types/Approval';

/**
 * @category Clerk API
 * @see fetchLiveCatalogStatus
 */
type FetchLiveCatalogStatusParams = {
    authToken: string;
    catalogId: number;
    deployment: string;
    sellerId: number;
};

type AuctionStatus = 'online' | 'live' | 'paused' | 'ended' | 'notLoaded' | 'unknown';

type CurrentLotStatus = 'open' | 'closed' | 'unknown';

export type PubSubConfig = {
    /** Write access key for the pub sub provider */
    publishKey: string;
    /** Read access key for the pub sub provider */
    subscribeKey: string;
};

type FetchLiveCatalogStatusResponsePayload = {
    auctionId: number;
    auctionStatus: AuctionStatus;
    bidderApproval: ApprovalStatus;
    bidderHasApprovalStatus: boolean;
    currentAskPrice: number;
    currentHighBid: number;
    currentIncrement: number;
    currentLotId: number;
    currentLotIndex: number;
    currentLotNumber: number;
    currentLotStatus: CurrentLotStatus;
    leadingBidderId: number;
    subscriptions: {
        ably: PubSubConfig;
        pubnub: PubSubConfig;
    };
};

type TransformFetchLiveCatalogStatusResponsePayload = {
    payload: FetchLiveCatalogStatusResponsePayload;
};

export type FetchLiveAuctionStatusResponse = {
    data: {
        bidder: {
            approval: ApprovalStatus;
            hasApprovalStatus: boolean;
            subscriptions: {
                ably: PubSubConfig;
                pubnub: PubSubConfig;
            };
        };
        catalogId: number;
        currentItem: {
            askPrice: number;
            increment: number;
            itemId: number;
            itemIndex: number;
            leadingBid: number;
            leadingBidder: number;
            lotNumber: number;
            lotStatus: CurrentLotStatus;
        };
        status: AuctionStatus;
    };
    error: boolean;
};

/**
 * Gets the live catalog status from auction engine
 * @function fetchLiveCatalogStatus
 * @category Clerk API
 * @param {FetchLiveCatalogStatusParams}
 * @returns {FetchLiveAuctionStatusResponse}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/clerk/houses/${sellerId}/auctions/${catalogId}/state/bidder
 */
export const fetchLiveCatalogStatus = ({ authToken, catalogId, deployment, sellerId }: FetchLiveCatalogStatusParams) =>
    new Promise<FetchLiveAuctionStatusResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: `clerk/houses/${sellerId}/auctions/${catalogId}/state/bidder`,
            authToken,
            deployment,
            path: '<AUCTION-ENGINE-API>',
        });
        request.end((err: any, response: any) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                resolve,
                response,
                success404: true,
                transform: ({
                    payload,
                }: TransformFetchLiveCatalogStatusResponsePayload): FetchLiveAuctionStatusResponse => {
                    const {
                        auctionStatus,
                        bidderApproval,
                        bidderHasApprovalStatus,
                        currentAskPrice,
                        currentHighBid,
                        currentIncrement,
                        currentLotId,
                        currentLotIndex,
                        currentLotNumber,
                        currentLotStatus,
                        leadingBidderId,
                        subscriptions,
                    } = payload || {};

                    return {
                        data: {
                            bidder: {
                                approval: bidderApproval,
                                hasApprovalStatus: bidderHasApprovalStatus,
                                subscriptions,
                            },
                            catalogId,
                            currentItem: {
                                askPrice: currentAskPrice,
                                increment: currentIncrement,
                                itemId: currentLotId,
                                itemIndex: currentLotIndex,
                                leadingBid: currentHighBid,
                                leadingBidder: leadingBidderId,
                                lotNumber: currentLotNumber,
                                lotStatus: currentLotStatus,
                            },
                            status: auctionStatus,
                        },
                        error: false,
                    };
                },
            })
        );
    });

/**
 * @category Clerk API
 * @see fetchLiveCatalogStatusFromPubnub
 */
type FetchLiveCatalogStatusFromPubnubParams = {
    channel: string;
    subscribeKey: string;
};

/**
 * fetches live catalog status via Get request to Clerk API Service
 * @function fetchLiveCatalogStatusFromPubnub
 * @category Pubnub API
 * @param {fetcLiveCatalogStatusFromPubnub}
 * @see https://ps.pndsn.com/v2/presence/sub-key/${subscribeKey}/channel/${channel}
 */
export const fetchLiveCatalogStatusFromPubnub = ({ channel, subscribeKey }: FetchLiveCatalogStatusFromPubnubParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            path: `<PUBNUB-API>${subscribeKey}/channel/${channel}`,
        });

        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve,
                response: { body: response },
                transform: ({ text }: { text: string }) => JSON.parse(text),
            })
        );
    });
